import { useEffect, useRef, useState } from 'react';
import { MemberTagRules } from './MemberTagRules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberTagDefinitionDto,
  MemberTagInput,
  MemberTagLegacyInput,
  MemberTagPreview,
  MemberTagResult,
  MemberTagScope,
} from '@pokerroom/types';
import { api, apiClient } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

type Filter = {
  tagIds: string[];
  tagMatch: 'ANY' | 'ALL';
  tagScope: 'EFFECTIVE' | 'ASSOCIATION' | 'CURRENT_VENUE';
};
type Assignment = {
  id: string;
  definition: MemberTagDefinitionDto;
  scope: MemberTagScope;
  source: string;
  assignedAt: string;
  assignedBy: string | null;
  automatic?: {
    ruleId: string;
    revisionNumber: number;
    computedAt: string;
    stale: boolean;
    observationStart: string | null;
    active: boolean;
  }[];
  expiresAt: string | null;
  removedAt: string | null;
  supersededAt: string | null;
};
type Candidate = {
  sourceRecordId: string;
  memberNo: string;
  displayName: string;
  rawValue: string;
  blocked: boolean;
  decisions: {
    revision: number;
    rawValueHash: string;
    outcome: string;
    targetTagIds: string[];
    exclusionReason: string | null;
  }[];
};
const control = 'border border-base-300 rounded px-3 py-2 bg-base-100 max-w-full';
const FILTER_SCOPES = ['EFFECTIVE', 'ASSOCIATION', 'CURRENT_VENUE'] as const;
const TAG_SEARCH_THRESHOLD = 10;

// One catalogue request shared by the filter field and the management section.
const catalogueQuery = (session: unknown, page: number) => ({
  queryKey: ['member-tags', session, page],
  queryFn: () =>
    api.get<{ data: MemberTagDefinitionDto[]; total: number }>(
      `/member-tags?page=${page}&archived=true`,
    ),
});

// The member list API accepts at most this many tag IDs (member-tags.common.ts tagFilter).
const MAX_SELECTED_TAGS = 100;

// Active tags for the filter field: every catalogue page, archived definitions excluded by the API.
const activeCatalogueQuery = (session: unknown) => ({
  queryKey: ['member-tags', session, 'active'],
  queryFn: async () => {
    const rows: MemberTagDefinitionDto[] = [];
    for (let page = 1; ; page += 1) {
      const result = await api.get<{ data: MemberTagDefinitionDto[]; total: number }>(
        `/member-tags?page=${page}`,
      );
      rows.push(...result.data);
      if (result.data.length === 0 || rows.length >= result.total) break;
    }
    return rows;
  },
});

const errorCode = (e: unknown) =>
  (e as { response?: { data?: { code?: string } } }).response?.data?.code;

/** Tag filter field for the member list toolbar: checkbox list, match toggle, scope and chips. */
export function MemberTagFilter({
  filter,
  onFilter,
}: {
  filter: Filter;
  onFilter: (value: Filter) => void;
}) {
  const { t } = useTranslation('member');
  const session = useAuthStore((s) => s.sessionRevision);
  const catalogue = useQuery(activeCatalogueQuery(session));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const tags = catalogue.data ?? [];
  // Remember names so a selected tag archived meanwhile keeps a readable, removable chip.
  const names = useRef(new Map<string, string>());
  useEffect(() => {
    for (const tag of catalogue.data ?? []) names.current.set(tag.id, tag.name);
  }, [catalogue.data]);
  const activeNames = new Map(tags.map((tag) => [tag.id, tag.name]));
  const selected = filter.tagIds.map((id) => {
    const name = activeNames.get(id) ?? names.current.get(id) ?? t('tags.unknownTag');
    return { id, name, archived: !activeNames.has(id) };
  });
  const visible = query.trim()
    ? tags.filter((tag) => tag.name.toLowerCase().includes(query.trim().toLowerCase()))
    : tags;
  const unavailable = catalogue.isLoading || catalogue.isError || tags.length === 0;
  const summary = catalogue.isLoading
    ? t('tags.loading')
    : catalogue.isError
      ? t('tags.loadFailed')
      : tags.length === 0
        ? t('tags.noTags')
        : filter.tagIds.length
          ? t('tags.filterSelected', { count: filter.tagIds.length })
          : t('tags.filterAll');
  const atLimit = filter.tagIds.length >= MAX_SELECTED_TAGS;
  const toggle = (id: string) => {
    if (filter.tagIds.includes(id))
      onFilter({ ...filter, tagIds: filter.tagIds.filter((tagId) => tagId !== id) });
    else if (!atLimit) onFilter({ ...filter, tagIds: [...filter.tagIds, id] });
  };
  const close = () => {
    setOpen(false);
    document.getElementById('member-tag-button')?.focus();
  };
  return (
    <div className="contents">
      <div className="col-span-2 min-w-0 md:w-60">
        <span id="member-tag-label" className="block mb-2 text-sm font-medium text-base-content/80">
          {t('tags.field')}
        </span>
        <button
          id="member-tag-button"
          type="button"
          aria-labelledby="member-tag-label member-tag-button"
          aria-expanded={open && !unavailable}
          aria-controls="member-tag-panel"
          disabled={unavailable}
          onClick={() => setOpen(!open)}
          className="w-full min-h-10 flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-base-300 bg-base-200 text-base text-left text-base-content"
        >
          <span className="truncate">{summary}</span>
          <span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
        {catalogue.isError && (
          <button
            type="button"
            onClick={() => void catalogue.refetch()}
            className="min-h-11 text-sm text-primary hover:underline"
          >
            {t('tags.retry')}
          </button>
        )}
      </div>
      {selected.length > 0 && (
        <ul
          aria-label={t('tags.selected')}
          className="col-span-2 order-last md:basis-full flex flex-wrap gap-2"
        >
          {selected.map((tag) => (
            <li
              key={tag.id}
              className="inline-flex items-center border border-base-300 bg-base-200 text-sm"
            >
              <span className="pl-2 [overflow-wrap:anywhere]">
                {tag.archived ? `${tag.name} (${t('tags.archived')})` : tag.name}
              </span>
              <button
                type="button"
                aria-label={t('tags.removeTag', { name: tag.name })}
                onClick={() => toggle(tag.id)}
                className="inline-flex items-center justify-center w-11 min-h-11 hover:bg-base-300"
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !unavailable && (
        <div
          id="member-tag-panel"
          className="col-span-2 order-last md:basis-full border border-base-300 bg-base-200 p-3 space-y-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape') close();
          }}
        >
          {tags.length > TAG_SEARCH_THRESHOLD && (
            <input
              type="search"
              aria-label={t('tags.searchTags')}
              placeholder={t('tags.searchTags')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full md:w-72 px-3 py-2 rounded-md border border-base-300 bg-base-100 text-base"
            />
          )}
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-base-content/80">
              {t('tags.filter')}
            </legend>
            {atLimit && (
              <p role="status" className="mb-2 text-sm text-base-content/70">
                {t('tags.limitReached', { max: MAX_SELECTED_TAGS })}
              </p>
            )}
            <ul className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-3 max-h-80 overflow-auto">
              {visible.map((tag) => (
                <li key={tag.id}>
                  <label className="flex min-h-11 items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filter.tagIds.includes(tag.id)}
                      disabled={atLimit && !filter.tagIds.includes(tag.id)}
                      onChange={() => toggle(tag.id)}
                    />
                    <span className="[overflow-wrap:anywhere]">{tag.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          <div className="flex flex-wrap items-end gap-4">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-base-content/80">
                {t('tags.match')}
              </legend>
              <div className="inline-flex border border-base-300">
                {(['ANY', 'ALL'] as const).map((v) => (
                  <label
                    key={v}
                    className={`inline-flex min-h-11 items-center px-3 text-sm cursor-pointer has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-primary ${filter.tagMatch === v ? 'bg-primary text-primary-content' : 'text-base-content hover:bg-base-300'}`}
                  >
                    <input
                      type="radio"
                      name="member-tag-match"
                      value={v}
                      checked={filter.tagMatch === v}
                      onChange={() => onFilter({ ...filter, tagMatch: v })}
                      className="sr-only"
                    />
                    {t(`tags.${v}`)}
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label
                htmlFor="member-tag-scope"
                className="block mb-2 text-sm font-medium text-base-content/80"
              >
                {t('tags.scope')}
              </label>
              <select
                id="member-tag-scope"
                value={filter.tagScope}
                onChange={(e) =>
                  onFilter({ ...filter, tagScope: e.target.value as Filter['tagScope'] })
                }
                className="px-3 py-2 rounded-md border border-base-300 bg-base-100 text-base"
              >
                {FILTER_SCOPES.map((v) => (
                  <option key={v} value={v}>
                    {t(`tags.${v}`)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={close}
              className="min-h-10 px-3 rounded-md border border-base-300 text-sm hover:bg-base-300"
            >
              {t('tags.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Export of the members matching the current list filters; only for roles allowed to export. */
export function MemberTagExport({
  filter,
  exportFilter,
}: {
  filter: Filter;
  exportFilter: { search?: string; levelId?: string; status?: string };
}) {
  const { t } = useTranslation('member');
  const actions = useAuthStore((s) => s.allowedActions);
  const [error, setError] = useState('');
  const exportRows = useMutation({
    mutationFn: async () => {
      setError('');
      const response = await apiClient.post<Blob>(
        '/member-tags/export',
        { ...exportFilter, ...filter },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'member-tags.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (e) =>
      setError(
        t(`tags.errors.${errorCode(e) ?? 'FAILED'}`, { defaultValue: t('tags.errors.FAILED') }),
      ),
  });
  if (!actions?.includes('member:tags_export')) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <button
        type="button"
        aria-describedby="member-tag-export-scope"
        disabled={exportRows.isPending}
        onClick={() => exportRows.mutate()}
        className="inline-flex min-h-10 items-center px-3 rounded-md border border-base-300 text-sm text-base-content hover:bg-base-300"
      >
        {t('tags.export')}
      </button>
      <span id="member-tag-export-scope" className="text-sm text-base-content/70">
        {t('tags.exportScope')}
      </span>
      {error && (
        <span role="alert" className="text-sm text-error">
          {error}
        </span>
      )}
    </div>
  );
}

function MemberTagsContent({
  memberId,
  memberNo,
  manageOpen,
}: {
  memberId?: string;
  memberNo?: string;
  /** When set, the caller owns the management toggle (member list header action). */
  manageOpen?: boolean;
}) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const session = useAuthStore((s) => s.sessionRevision);
  const can = (action: string) => actions?.includes(`member:tags_${action}`) ?? false;
  const [open, setOpen] = useState(false),
    [history, setHistory] = useState(false),
    [historyPage, setHistoryPage] = useState(1),
    [cataloguePage, setCataloguePage] = useState(1);
  const [error, setError] = useState(''),
    [name, setName] = useState(''),
    [description, setDescription] = useState(''),
    [edit, setEdit] = useState<MemberTagDefinitionDto | null>(null);
  const [scope, setScope] = useState<MemberTagScope>(can('assign') ? 'VENUE' : 'ASSOCIATION');
  const [tagId, setTagId] = useState(''),
    [action, setAction] = useState<MemberTagInput['action']>('ASSIGN'),
    [mode, setMode] = useState<MemberTagInput['identifierMode']>('MEMBER_NUMBER');
  const [identifiers, setIdentifiers] = useState(memberNo ?? ''),
    [expiresOn, setExpiresOn] = useState('');
  const [preview, setPreview] = useState<MemberTagPreview | null>(null),
    [frozen, setFrozen] = useState<MemberTagInput | MemberTagLegacyInput | null>(null),
    [result, setResult] = useState<MemberTagResult | null>(null);
  const [legacyPage, setLegacyPage] = useState(1),
    [legacyHistory, setLegacyHistory] = useState(false),
    [legacyOpen, setLegacyOpen] = useState(false);
  const [mapping, setMapping] = useState<
    Record<string, { tagIds: string[]; reason: string; selected: boolean; revision?: number }>
  >({});
  const changed = () => {
    setPreview(null);
    setFrozen(null);
    setResult(null);
    setError('');
  };
  const fail = (e: unknown) => {
    setError(
      t(`tags.errors.${errorCode(e) ?? 'FAILED'}`, { defaultValue: t('tags.errors.FAILED') }),
    );
    setPreview(null);
  };
  const catalogue = useQuery(catalogueQuery(session, cataloguePage));
  const tags = catalogue.data?.data ?? [];
  const detail = useQuery({
    queryKey: ['member-tags-detail', session, memberId, history, historyPage],
    enabled: !!memberId,
    queryFn: () =>
      api.get<{ data: Assignment[]; total: number }>(
        `/members/${memberId}/tags?history=${history}&page=${historyPage}`,
      ),
  });
  const legacy = useQuery({
    queryKey: ['member-tags-legacy', session, scope, legacyPage, legacyHistory],
    enabled: legacyOpen && can('import') && can(scope === 'VENUE' ? 'assign' : 'assign_global'),
    queryFn: () =>
      api.get<{ data: Candidate[]; hasMore: boolean }>(
        `/member-tags/legacy/candidates?scope=${scope}&page=${legacyPage}&history=${legacyHistory}`,
      ),
  });
  const refresh = () => {
    for (const key of ['member-tags', 'member-tags-detail', 'member-tags-legacy', 'members'])
      void qc.invalidateQueries({ queryKey: [key] });
  };
  const save = useMutation({
    mutationFn: () =>
      edit
        ? api.patch(`/member-tags/${edit.id}`, { name, description, version: edit.version })
        : api.post('/member-tags', { name, description }),
    onSuccess: () => {
      refresh();
      setName('');
      setDescription('');
      setEdit(null);
      changed();
    },
    onError: fail,
  });
  const archive = useMutation({
    mutationFn: (tag: MemberTagDefinitionDto) =>
      api.post(`/member-tags/${tag.id}/${tag.archivedAt ? 'restore' : 'archive'}`, {
        version: tag.version,
      }),
    onSuccess: () => {
      refresh();
      changed();
    },
    onError: fail,
  });
  const dry = useMutation({
    mutationFn: (body: MemberTagInput | MemberTagLegacyInput) =>
      api.post<MemberTagPreview>(
        `/member-tags/${'mappings' in body ? 'legacy' : 'operations'}/preview`,
        body,
      ),
    onSuccess: (value, body) => {
      setPreview(value);
      setFrozen(body);
      setResult(null);
      setError('');
    },
    onError: fail,
  });
  const confirm = useMutation({
    mutationFn: () =>
      api.post<MemberTagResult>(
        `/member-tags/${frozen && 'mappings' in frozen ? 'legacy' : 'operations'}/confirm`,
        { ...frozen, token: preview!.token },
      ),
    onSuccess: (value) => {
      setResult(value);
      setPreview(null);
      setFrozen(null);
      setMapping({});
      refresh();
    },
    onError: fail,
  });
  const pending = dry.isPending || confirm.isPending || save.isPending || archive.isPending;
  const assignAllowed = can(scope === 'VENUE' ? 'assign' : 'assign_global');
  const legacyPreview = () => {
    const selected = (legacy.data?.data ?? []).filter(
      (row) => mapping[row.sourceRecordId]?.selected,
    );
    const revisions = selected.some((row) => mapping[row.sourceRecordId]?.revision !== undefined);
    dry.mutate({
      operationKey: crypto.randomUUID(),
      action: revisions ? 'REVISE_DECISION' : 'CONVERT',
      scope,
      mappings: selected.map((row) => ({
        sourceRecordId: row.sourceRecordId,
        rawValue: row.rawValue,
        targetTagIds: mapping[row.sourceRecordId]!.tagIds,
        exclusionReason: mapping[row.sourceRecordId]!.reason,
        decisionRevision: mapping[row.sourceRecordId]!.revision,
      })),
    });
  };
  const isOpen = manageOpen ?? open;
  if (manageOpen === false && !memberId) return null;
  return (
    <section
      id="member-tags-manage"
      className={`my-4 border border-base-300 space-y-2 min-w-0 ${memberId ? 'p-3' : 'rounded p-3'}`}
      aria-label={t('tags.title')}
    >
      <h2 className="font-semibold">{t('tags.title')}</h2>
      {!memberId ? <MemberTagRules tags={tags} /> : null}
      {memberId && (
        <div className="space-y-2">
          {(isOpen || history) && (
            <label className="inline-flex min-h-11 items-center gap-2 text-sm text-base-content">
              <input
                type="checkbox"
                className="h-5 w-5 accent-primary"
                checked={history}
                onChange={(e) => {
                  setHistory(e.target.checked);
                  setHistoryPage(1);
                }}
              />
              {t('tags.history')}
            </label>
          )}
          {detail.data?.data.length === 0 && (
            <p className="text-sm text-base-content/70">
              {t(history ? 'tags.empty' : 'tags.noneAssigned')}
            </p>
          )}
          <ul className="flex flex-wrap gap-2">
            {detail.data?.data.map((row) => (
              <li
                key={row.id}
                className="min-w-0 max-w-full break-words border border-base-300 bg-base-200 px-3 py-2"
              >
                <span className="block text-sm font-medium text-base-content">
                  {row.definition.name}
                </span>
                <span className="block text-xs text-base-content/70">
                  {t(`tags.${row.scope}`)} · {t(`tags.${row.source}`)} ·{' '}
                  {row.expiresAt
                    ? t('tags.expires', { date: new Date(row.expiresAt).toLocaleString() })
                    : t('tags.noExpiry')}
                </span>
                {row.automatic?.map((proof) => (
                  <p className="text-sm" key={proof.ruleId + proof.computedAt}>
                    {t('tagRules.provenance', {
                      rule: proof.ruleId,
                      revision: proof.revisionNumber,
                      date: new Date(proof.computedAt).toLocaleString(),
                    })}{' '}
                    · {proof.stale ? t('tagRules.stale') : t('tagRules.SUCCEEDED')} ·{' '}
                    {proof.observationStart
                      ? t('tagRules.observed', {
                          date: new Date(proof.observationStart).toLocaleString(),
                        })
                      : t('tagRules.UNKNOWN')}
                    {history && !proof.active && ` · ${t('tagRules.previousGeneration')}`}
                  </p>
                ))}
                {history && row.assignedBy && (
                  <p className="text-xs text-base-content/70">
                    {t('tags.assigned', {
                      date: new Date(row.assignedAt).toLocaleString(),
                      actor: row.assignedBy,
                    })}{' '}
                    ·{' '}
                    {row.removedAt
                      ? t('tags.removed')
                      : row.supersededAt
                        ? t('tags.superseded')
                        : row.definition.archivedAt
                          ? t('tags.archived')
                          : row.expiresAt && new Date(row.expiresAt) <= new Date()
                            ? t('tags.expired')
                            : t('tags.active')}
                  </p>
                )}
              </li>
            ))}
          </ul>
          {(detail.data?.total ?? 0) > 100 && (
            <div>
              <button
                className={control}
                disabled={historyPage === 1}
                onClick={() => setHistoryPage((p) => p - 1)}
              >
                {t('tags.previous')}
              </button>
              <button
                className={control}
                disabled={historyPage * 100 >= (detail.data?.total ?? 0)}
                onClick={() => setHistoryPage((p) => p + 1)}
              >
                {t('tags.next')}
              </button>
            </div>
          )}
        </div>
      )}
      {manageOpen === undefined && (
        <button
          type="button"
          className="inline-flex min-h-11 items-center text-sm text-primary underline underline-offset-4 hover:text-base-content"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {t('tags.manage')}
        </button>
      )}
      {isOpen && (
        <fieldset disabled={pending} className="space-y-4 min-w-0">
          <div className="space-y-2">
            <h3>{t('tags.catalogue')}</h3>
            {catalogue.isLoading && <p>{t('tags.loading')}</p>}
            {tags.length === 0 && <p>{t('tags.empty')}</p>}
            {tags.map((tag) => (
              <div key={tag.id} className="flex flex-wrap gap-2 items-center">
                <span className="break-words">
                  {tag.name} {tag.archivedAt && `(${t('tags.archived')})`}
                </span>
                {can('manage') && (
                  <>
                    <button
                      className={control}
                      onClick={() => {
                        setEdit(tag);
                        setName(tag.name);
                        setDescription(tag.description);
                      }}
                    >
                      {t('tags.edit')}
                    </button>
                    <button
                      className={control}
                      onClick={() => {
                        if (
                          window.confirm(
                            t(tag.archivedAt ? 'tags.restoreWarning' : 'tags.archiveWarning'),
                          )
                        )
                          archive.mutate(tag);
                      }}
                    >
                      {t(tag.archivedAt ? 'tags.restore' : 'tags.archive')}
                    </button>
                  </>
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <button
                className={control}
                disabled={cataloguePage === 1}
                onClick={() => setCataloguePage((p) => p - 1)}
              >
                {t('tags.previous')}
              </button>
              <button
                className={control}
                disabled={cataloguePage * 100 >= (catalogue.data?.total ?? 0)}
                onClick={() => setCataloguePage((p) => p + 1)}
              >
                {t('tags.next')}
              </button>
            </div>
            {can('manage') && (
              <div className="flex flex-wrap gap-2">
                <label>
                  {t('tags.name')}
                  <input
                    className={`${control} block`}
                    maxLength={64}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
                <label>
                  {t('tags.description')}
                  <input
                    className={`${control} block`}
                    maxLength={500}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </label>
                <button className={control} disabled={!name.trim()} onClick={() => save.mutate()}>
                  {t(edit ? 'tags.save' : 'tags.create')}
                </button>
                {edit && (
                  <button
                    className={control}
                    onClick={() => {
                      setEdit(null);
                      setName('');
                      setDescription('');
                    }}
                  >
                    {t('tags.cancel')}
                  </button>
                )}
              </div>
            )}
          </div>
          {(can('assign') || can('assign_global')) && (
            <div className="space-y-2">
              <h3>{t('tags.bulk')}</h3>
              <p>{t('tags.bulkHelp')}</p>
              <div className="flex flex-wrap gap-2">
                <label>
                  {t('tags.scope')}
                  <select
                    className={`${control} block`}
                    value={scope}
                    onChange={(e) => {
                      setScope(e.target.value as MemberTagScope);
                      changed();
                      setMapping({});
                    }}
                  >
                    {can('assign') && <option value="VENUE">{t('tags.VENUE')}</option>}
                    {can('assign_global') && (
                      <option value="ASSOCIATION">{t('tags.ASSOCIATION')}</option>
                    )}
                  </select>
                </label>
                <label>
                  {t('tags.tag')}
                  <select
                    className={`${control} block`}
                    value={tagId}
                    onChange={(e) => {
                      setTagId(e.target.value);
                      changed();
                    }}
                  >
                    <option value="">{t('tags.choose')}</option>
                    {tags.map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                        {tag.archivedAt ? ` (${t('tags.archived')})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('tags.action')}
                  <select
                    className={`${control} block`}
                    value={action}
                    onChange={(e) => {
                      setAction(e.target.value as MemberTagInput['action']);
                      changed();
                    }}
                  >
                    {['ASSIGN', 'REMOVE', 'SET_EXPIRY'].map((v) => (
                      <option key={v} value={v}>
                        {t(`tags.${v}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('tags.identity')}
                  <select
                    className={`${control} block`}
                    value={mode}
                    onChange={(e) => {
                      setMode(e.target.value as MemberTagInput['identifierMode']);
                      changed();
                    }}
                  >
                    {['MEMBER_NUMBER', 'PHONE'].map((v) => (
                      <option key={v} value={v}>
                        {t(`tags.${v}`)}
                      </option>
                    ))}
                  </select>
                </label>
                {action !== 'REMOVE' && (
                  <label>
                    {t('tags.expiry')}
                    <input
                      type="date"
                      className={`${control} block`}
                      value={expiresOn}
                      onChange={(e) => {
                        setExpiresOn(e.target.value);
                        changed();
                      }}
                    />
                  </label>
                )}
              </div>
              <label className="block">
                {t('tags.identifiers')}
                <textarea
                  className={`${control} block w-full`}
                  rows={4}
                  value={identifiers}
                  onChange={(e) => {
                    setIdentifiers(e.target.value);
                    changed();
                  }}
                />
              </label>
              <button
                className={control}
                disabled={!assignAllowed || !tagId || !identifiers.trim()}
                onClick={() =>
                  dry.mutate({
                    operationKey: crypto.randomUUID(),
                    scope,
                    action,
                    tagId,
                    identifierMode: mode,
                    identifiers: identifiers
                      .split(/\r?\n/)
                      .map((s) => s.trim())
                      .filter(Boolean),
                    expiresOn: expiresOn || null,
                  })
                }
              >
                {t('tags.preview')}
              </button>
            </div>
          )}
          {can('import') && assignAllowed && (
            <div className="space-y-2">
              <button
                className={control}
                onClick={() => setLegacyOpen(!legacyOpen)}
                aria-expanded={legacyOpen}
              >
                {t('tags.legacy')}
              </button>
              {legacyOpen && (
                <>
                  <p>{t('tags.legacyHelp')}</p>
                  <label>
                    <input
                      type="checkbox"
                      checked={legacyHistory}
                      onChange={(e) => {
                        setLegacyHistory(e.target.checked);
                        setLegacyPage(1);
                        setMapping({});
                        changed();
                      }}
                    />{' '}
                    {t('tags.decisions')}
                  </label>
                  {legacy.data?.data.length === 0 && <p>{t('tags.empty')}</p>}
                  {legacy.data?.data.map((row) => {
                    const value = mapping[row.sourceRecordId] ?? {
                      tagIds: [],
                      reason: '',
                      selected: false,
                    };
                    const update = (next: typeof value) => {
                      setMapping((current) => ({ ...current, [row.sourceRecordId]: next }));
                      changed();
                    };
                    return (
                      <div
                        key={row.sourceRecordId}
                        className="border border-base-300 p-2 space-y-2 break-words"
                      >
                        <label>
                          <input
                            type="checkbox"
                            disabled={row.blocked}
                            checked={value.selected}
                            onChange={(e) => update({ ...value, selected: e.target.checked })}
                          />{' '}
                          {row.memberNo} · {row.displayName} · {row.rawValue}
                        </label>
                        {row.blocked && <p>{t('tags.bindingChanged')}</p>}
                        <label className="block">
                          {t('tags.targets')}
                          <select
                            multiple
                            className={`${control} block`}
                            value={value.tagIds}
                            onChange={(e) =>
                              update({
                                ...value,
                                tagIds: Array.from(e.target.selectedOptions).map((o) => o.value),
                              })
                            }
                          >
                            {tags
                              .filter((tag) => !tag.archivedAt)
                              .map((tag) => (
                                <option key={tag.id} value={tag.id}>
                                  {tag.name}
                                </option>
                              ))}
                          </select>
                        </label>
                        <label className="block">
                          {t('tags.exclusion')}
                          <input
                            className={`${control} block w-full`}
                            maxLength={500}
                            value={value.reason}
                            onChange={(e) => update({ ...value, reason: e.target.value })}
                          />
                        </label>
                        {row.decisions.map((decision, i) => (
                          <p key={i}>
                            {t(`tags.${decision.outcome}`)} ·{' '}
                            {t('tags.revision', { revision: decision.revision })}{' '}
                            {decision.exclusionReason}
                            <button
                              className={control}
                              onClick={() =>
                                update({
                                  ...value,
                                  revision: decision.revision,
                                  selected: true,
                                  tagIds: decision.targetTagIds,
                                })
                              }
                            >
                              {t('tags.revise')}
                            </button>
                          </p>
                        ))}
                      </div>
                    );
                  })}
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={control}
                      disabled={legacyPage === 1}
                      onClick={() => {
                        setLegacyPage((p) => p - 1);
                        setMapping({});
                        changed();
                      }}
                    >
                      {t('tags.previous')}
                    </button>
                    <button
                      className={control}
                      disabled={!legacy.data?.hasMore}
                      onClick={() => {
                        setLegacyPage((p) => p + 1);
                        setMapping({});
                        changed();
                      }}
                    >
                      {t('tags.next')}
                    </button>
                    <button
                      className={control}
                      disabled={!Object.values(mapping).some((v) => v.selected)}
                      onClick={legacyPreview}
                    >
                      {t('tags.preview')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </fieldset>
      )}
      {(error || catalogue.isError || detail.isError || legacy.isError) && (
        <p role="alert" className="text-error">
          {error || t('tags.errors.FAILED')}
        </p>
      )}
      {preview && (
        <div className="space-y-2">
          <h3>{t('tags.previewTitle')}</h3>
          <p>{t('tags.duplicates', { count: preview.duplicateCount })}</p>
          <p>{t('tags.previewExpiry')}</p>
          <ul>
            {preview.errors.map((e, i) => (
              <li key={i} className="text-error">
                {t('tags.rowError', {
                  row: e.row + 1,
                  reason: t(`tags.errors.${e.code}`, { defaultValue: t('tags.errors.FAILED') }),
                })}
              </li>
            ))}
          </ul>
          <ul className="max-h-80 overflow-auto">
            {preview.rows.map((row) => (
              <li key={row.memberId}>
                {row.memberNo} · {row.displayName} · {t(`status_${row.status}`)} ·{' '}
                {t('tags.changes', { changed: row.changes, noops: row.noops })}
              </li>
            ))}
          </ul>
          <button
            className={control}
            disabled={!preview.token || pending}
            onClick={() => confirm.mutate()}
          >
            {t('tags.confirm')}
          </button>
        </div>
      )}
      {result && (
        <p role="status">{t('tags.changes', { changed: result.changed, noops: result.noops })}</p>
      )}
    </section>
  );
}

export function MemberTags(props: Parameters<typeof MemberTagsContent>[0]) {
  const session = useAuthStore((s) => s.sessionRevision);
  return <MemberTagsContent key={session} {...props} />;
}
